<template>
  <div>
    <StockDocumentPageHeading
      :title="$t('STOCK.REVISIONS.PAGE_TITLE')"
      :code="revision ? revision.code : ''"
      :new-label="revision ? '' : $t('STOCK.REVISIONS.NEW_LABEL')"
    />

    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>

      <v-stepper v-model="step">
        <v-stepper-header>
          <v-stepper-step step="1" :complete="step > 1" editable @click="goToStep(1)">
            {{ $t("STOCK.REVISIONS.STEPS.SCOPE") }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="2" :complete="step > 2" :editable="Boolean(revision)" @click="goToStep(2)">
            {{ $t("STOCK.REVISIONS.STEPS.COUNT") }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="3" :editable="Boolean(revision)" @click="goToStep(3)">
            {{ $t("STOCK.REVISIONS.STEPS.REVIEW") }}
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-row dense>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.organization_id"
                  :items="organizationOptions"
                  item-text="label"
                  item-value="id"
                  :label="$t('STOCK.REVISIONS.COMPANY') + '*'"
                  :disabled="scopeLocked"
                  :error-messages="validationErrors.organization_id"
                  dense
                  @change="handleOrganizationChange"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.warehouse_id"
                  :items="warehouseOptions"
                  item-text="label"
                  item-value="id"
                  :label="$t('STOCK.REVISIONS.WAREHOUSE') + '*'"
                  :hint="$t('STOCK.REVISIONS.SCOPE_HINT')"
                  persistent-hint
                  :disabled="scopeLocked || !form.organization_id"
                  :error-messages="validationErrors.warehouse_id"
                  dense
                  @change="handleWarehouseChange"
                ></v-autocomplete>
              </v-col>
              <v-col v-if="!isEditLastMode" cols="12" md="4">
                <DatePicker
                  v-model="form.for_date"
                  :label="$t('STOCK.REVISIONS.FOR_DATE') + '*'"
                  :disabled="scopeLocked"
                  :error-messages="validationErrors.for_date"
                  :hint="$t('STOCK.REVISIONS.FOR_DATE_HINT')"
                  without-hint
                />
                <div class="caption grey--text text--darken-1">
                  {{ $t("STOCK.REVISIONS.FOR_DATE_HINT") }}
                </div>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.notes"
                  :label="$t('STOCK.REVISIONS.NOTES')"
                  rows="2"
                  auto-grow
                  dense
                ></v-textarea>
              </v-col>
            </v-row>

            <v-alert
              v-if="warehouseState && warehouseState.latest_finalized"
              type="info"
              dense
              outlined
            >
              {{
                $t("STOCK.REVISIONS.LATEST_FINALIZED", {
                  code: warehouseState.latest_finalized.code,
                  date: warehouseState.latest_finalized.for_date,
                })
              }}
              <span v-if="!isEditLastMode && warehouseState.earliest_allowed_for_date">
                ·
                {{
                  $t("STOCK.REVISIONS.EARLIEST_ALLOWED_DATE", {
                    date: warehouseState.earliest_allowed_for_date,
                  })
                }}
              </span>
            </v-alert>
            <v-alert
              v-else-if="warehouseState && form.warehouse_id"
              type="info"
              dense
              outlined
            >
              {{ $t("STOCK.REVISIONS.NO_FINALIZED_REVISION") }}
            </v-alert>

            <v-alert
              v-if="openDraft && !revision"
              type="warning"
              dense
              outlined
            >
              {{
                $t("STOCK.REVISIONS.OPEN_DRAFT_FOUND", {
                  code: openDraft.code,
                  date: openDraft.for_date,
                })
              }}
              <v-btn small text color="primary" class="ml-2" @click="continueOpenDraft">
                {{ $t("STOCK.REVISIONS.CONTINUE_DRAFT") }}
              </v-btn>
            </v-alert>

            <v-alert v-if="isEditLastMode" type="warning" dense outlined>
              {{ $t("STOCK.REVISIONS.EDIT_LAST_HINT") }}
            </v-alert>

            <div class="d-flex mt-2">
              <v-spacer></v-spacer>
              <v-btn text @click="$router.push({ name: 'stock-revisions' })">
                {{ $t("GENERAL.LABELS.CANCEL") }}
              </v-btn>
              <v-btn
                v-if="isEditLastMode && !revision"
                color="primary"
                class="ml-2"
                :disabled="!canReopen"
                :loading="isProcessing"
                @click="reopenLatest"
              >
                {{ $t("STOCK.REVISIONS.REOPEN") }}
              </v-btn>
              <v-btn
                v-else-if="!revision"
                color="primary"
                class="ml-2"
                :disabled="!canStart"
                :loading="isProcessing"
                @click="startRevision"
              >
                {{ $t("GENERAL.LABELS.CONTINUE_BUTTON") }}
              </v-btn>
              <v-btn v-else color="primary" class="ml-2" @click="goToStep(2)">
                {{ $t("GENERAL.LABELS.CONTINUE_BUTTON") }}
              </v-btn>
            </div>
          </v-stepper-content>

          <v-stepper-content step="2" class="pa-0">
            <StockRevisionCountSheet
              v-if="revision"
              ref="countSheet"
              :revision-id="revision.id"
              editable
              @dirty-change="hasUnsavedCounts = $event"
              @error="showError"
            />
            <div class="d-flex px-4 py-3">
              <v-btn text @click="goToStep(1)">
                {{ $t("GENERAL.LABELS.BACK_BUTTON") }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                v-if="revision && revision.status === 'draft'"
                text
                color="error"
                :loading="isProcessing"
                @click="showDeleteConfirm = true"
              >
                {{ $t("STOCK.REVISIONS.DELETE_DRAFT") }}
              </v-btn>
              <v-btn color="primary" class="ml-2" :loading="isProcessing" @click="goToStep(3)">
                {{ $t("GENERAL.LABELS.CONTINUE_BUTTON") }}
              </v-btn>
            </div>
          </v-stepper-content>

          <v-stepper-content step="3" class="pa-0">
            <v-card-text v-if="summary" class="pb-0">
              <div class="caption grey--text text--darken-1 mb-1">
                {{ $t("STOCK.REVISIONS.SUMMARY.TITLE") }}
              </div>
              <div class="d-flex flex-wrap">
                <v-chip small class="mr-2 mb-2" color="grey lighten-3">
                  <span class="font-weight-medium mr-1">
                    {{ $t("STOCK.REVISIONS.SUMMARY.TOTAL_ROWS") }}:
                  </span>
                  {{ summary.total_rows }}
                </v-chip>
                <v-chip small class="mr-2 mb-2" color="grey lighten-3">
                  <span class="font-weight-medium mr-1">
                    {{ $t("STOCK.REVISIONS.SUMMARY.COUNTED_ROWS") }}:
                  </span>
                  {{ summary.counted_rows }}
                </v-chip>
                <v-chip small class="mr-2 mb-2" color="grey lighten-3">
                  <span class="font-weight-medium mr-1">
                    {{ $t("STOCK.REVISIONS.SUMMARY.VARIANCE_ROWS") }}:
                  </span>
                  {{ summary.variance_rows }}
                </v-chip>
                <v-chip small class="mr-2 mb-2" color="grey lighten-3">
                  <span class="font-weight-medium mr-1">
                    {{ $t("STOCK.REVISIONS.SUMMARY.VARIANCE_QUANTITY") }}:
                  </span>
                  {{ formatSignedQuantity(summary.variance_quantity) }}
                </v-chip>
                <v-chip small class="mr-2 mb-2" color="grey lighten-3">
                  <span class="font-weight-medium mr-1">
                    {{ $t("STOCK.REVISIONS.SUMMARY.VARIANCE_VALUE") }}:
                  </span>
                  {{ formatSignedMoney(summary.variance_value, summary.currency_code) }}
                </v-chip>
                <v-chip small class="mr-2 mb-2" color="grey lighten-3">
                  <span class="font-weight-medium mr-1">
                    {{ $t("STOCK.REVISIONS.SUMMARY.CONSUMPTION_VALUE") }}:
                  </span>
                  {{ formatMoney(summary.consumption_value, summary.currency_code) }}
                </v-chip>
              </div>
            </v-card-text>

            <v-card-text v-if="warnings.length" class="pb-0">
              <v-alert type="warning" dense outlined class="mb-0">
                <div class="font-weight-medium">{{ $t("STOCK.REVISIONS.WARNINGS.TITLE") }}</div>
                <div class="caption mb-2">{{ $t("STOCK.REVISIONS.WARNINGS.HINT") }}</div>
                <div v-for="warning in warnings" :key="warning.stock_product_id" class="caption">
                  {{ warningProductLabel(warning) }} ·
                  {{ $t("STOCK.REVISIONS.WARNINGS.RESERVED") }}:
                  {{ formatQuantity(warning.reserved_quantity) }} {{ warning.unit_code }} ·
                  {{ $t("STOCK.REVISIONS.WARNINGS.SHORTFALL") }}:
                  {{ formatQuantity(warning.shortfall_quantity) }} {{ warning.unit_code }}
                </div>
              </v-alert>
            </v-card-text>

            <StockRevisionCountSheet
              v-if="revision"
              ref="reviewSheet"
              :key="`review-${reviewSheetKey}`"
              :revision-id="revision.id"
              @error="showError"
            />

            <div class="d-flex px-4 py-3">
              <v-btn text @click="goToStep(2)">
                {{ $t("GENERAL.LABELS.BACK_BUTTON") }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="success"
                :loading="isProcessing"
                @click="showFinalizeConfirm = true"
              >
                {{ $t("STOCK.REVISIONS.FINALIZE") }}
              </v-btn>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>

    <TPConfirm
      v-model="showFinalizeConfirm"
      :content="finalizeConfirmMessage"
      :confirm-label="$t('STOCK.REVISIONS.FINALIZE')"
      @confirm="finalizeRevision"
    ></TPConfirm>

    <TPConfirm
      v-model="showDeleteConfirm"
      :content="deleteConfirmMessage"
      :confirm-label="$t('STOCK.REVISIONS.DELETE_DRAFT')"
      @confirm="deleteDraft"
    ></TPConfirm>
  </div>
</template>

<script>
import moment from "moment";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockRevisionCountSheet from "@/views/partials/stock/revisions/StockRevisionCountSheet.vue";
import stockRevisionDisplayMixin from "@/views/partials/stock/revisions/stockRevisionDisplay.mixin";
import { GET_ORGANIZATIONS_OPTIONS } from "@/store/organizations.module";
import { GET_STOCK_WAREHOUSE_OPTIONS } from "@/store/stock/stockWarehouses.module";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  ACCEPT_STOCK_REVISION_DEFAULT_COUNTS,
  DELETE_STOCK_REVISION,
  FINALIZE_STOCK_REVISION,
  GET_STOCK_REVISION,
  GET_STOCK_REVISION_SUMMARY,
  GET_STOCK_REVISION_WAREHOUSE_STATE,
  GET_STOCK_REVISION_WARNINGS,
  REOPEN_LATEST_STOCK_REVISION,
  START_STOCK_REVISION,
} from "@/store/stock/stockRevisions.module";

export default {
  name: "StockRevisionWizard",
  components: {
    DatePicker,
    TPConfirm,
    StockDocumentPageHeading,
    StockRevisionCountSheet,
  },
  mixins: [stockRevisionDisplayMixin],
  data() {
    return {
      step: 1,
      isProcessing: false,
      revision: null,
      warehouseState: null,
      organizationOptions: [],
      warehouseOptions: [],
      validationErrors: {},
      summary: null,
      warnings: [],
      hasUnsavedCounts: false,
      reviewSheetKey: 0,
      showFinalizeConfirm: false,
      showDeleteConfirm: false,
      form: {
        organization_id: null,
        warehouse_id: null,
        for_date: moment().format("YYYY-MM-DD"),
        notes: "",
      },
    };
  },
  computed: {
    isEditLastMode() {
      return this.$route.name === "stock-revisions-edit-last";
    },
    revisionId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null;
    },
    openDraft() {
      return this.warehouseState ? this.warehouseState.open_draft : null;
    },
    scopeLocked() {
      return Boolean(this.revision);
    },
    canStart() {
      return Boolean(this.form.organization_id && this.form.warehouse_id && this.form.for_date);
    },
    canReopen() {
      return Boolean(
        this.form.warehouse_id && this.warehouseState && this.warehouseState.latest_finalized
      );
    },
    finalizeConfirmMessage() {
      if (!this.revision) {
        return "";
      }
      return this.$t("STOCK.REVISIONS.FINALIZE_CONFIRM", {
        code: this.revision.code,
        date: this.revision.for_date,
      });
    },
    deleteConfirmMessage() {
      if (!this.revision) {
        return "";
      }
      return this.$t("STOCK.REVISIONS.DELETE_DRAFT_CONFIRM", { code: this.revision.code });
    },
  },
  async mounted() {
    await this.loadOrganizations();

    if (this.revisionId) {
      await this.loadRevision(this.revisionId);
    } else {
      this.applyContextDefaults();
    }
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedCounts) {
      next();
      return;
    }

    this.flushCountSheet().finally(() => next());
  },
  methods: {
    async loadOrganizations() {
      const contextIds = (this.$store.getters.stockContextOrganizationIds || []).map(Number);
      const params = {};
      if (contextIds.length) {
        params.organization_ids = contextIds;
      }

      try {
        const response = await this.$store.dispatch(GET_ORGANIZATIONS_OPTIONS, params);
        const list = response.data || response;
        this.organizationOptions = list.map((organization) => ({
          id: organization.id,
          label: organization.name || organization.code || `#${organization.id}`,
        }));
      } catch (error) {
        this.showError(error);
      }
    },
    async loadWarehouses() {
      if (!this.form.organization_id) {
        this.warehouseOptions = [];
        return;
      }

      try {
        const response = await this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, {
          organization_ids: [this.form.organization_id],
        });
        const list = response.data || response;
        this.warehouseOptions = list
          .filter((warehouse) => warehouse.type === "physical")
          .map((warehouse) => ({
            id: warehouse.id,
            label:
              warehouse.label ||
              (warehouse.code && warehouse.name
                ? `${warehouse.code} — ${warehouse.name}`
                : warehouse.name || warehouse.code || `#${warehouse.id}`),
          }));
      } catch (error) {
        this.showError(error);
      }
    },
    applyContextDefaults() {
      const contextOrgIds = (this.$store.getters.stockContextOrganizationIds || []).map(Number);
      if (contextOrgIds.length === 1) {
        this.form.organization_id = contextOrgIds[0];
        this.handleOrganizationChange();
      }
    },
    async handleOrganizationChange() {
      this.form.warehouse_id = null;
      this.warehouseState = null;
      await this.loadWarehouses();

      const contextWarehouseIds = (this.$store.getters.stockContextWarehouseIds || []).map(Number);
      const single = this.warehouseOptions.filter((warehouse) =>
        contextWarehouseIds.length ? contextWarehouseIds.includes(Number(warehouse.id)) : true
      );
      if (single.length === 1) {
        this.form.warehouse_id = single[0].id;
        await this.handleWarehouseChange();
      }
    },
    async handleWarehouseChange() {
      this.warehouseState = null;
      if (!this.form.warehouse_id) {
        return;
      }

      try {
        this.warehouseState = await this.$store.dispatch(GET_STOCK_REVISION_WAREHOUSE_STATE, {
          warehouse_id: this.form.warehouse_id,
        });

        if (
          !this.isEditLastMode &&
          this.warehouseState.earliest_allowed_for_date &&
          moment(this.form.for_date).isBefore(this.warehouseState.earliest_allowed_for_date)
        ) {
          this.form.for_date = this.warehouseState.earliest_allowed_for_date;
        }
      } catch (error) {
        this.showError(error);
      }
    },
    async loadRevision(id) {
      this.isProcessing = true;
      try {
        const revision = await this.$store.dispatch(GET_STOCK_REVISION, id);
        this.applyRevision(revision);
        this.step = 2;
      } catch (error) {
        this.showError(error);
      } finally {
        this.isProcessing = false;
      }
    },
    applyRevision(revision) {
      this.revision = revision;
      this.form.organization_id = revision.organization_id;
      this.form.warehouse_id = revision.warehouse_id;
      this.form.for_date = revision.for_date;
      this.form.notes = revision.notes || "";
      this.loadWarehouses();
    },
    async startRevision() {
      this.isProcessing = true;
      this.validationErrors = {};

      try {
        const revision = await this.$store.dispatch(START_STOCK_REVISION, {
          organization_id: this.form.organization_id,
          warehouse_id: this.form.warehouse_id,
          for_date: this.form.for_date,
          notes: this.form.notes || null,
        });
        this.applyRevision(revision);
        this.replaceRouteWithRevision();
        this.step = 2;
      } catch (error) {
        this.validationErrors = (error && error.errors) || {};
        this.showError(error);
      } finally {
        this.isProcessing = false;
      }
    },
    async reopenLatest() {
      this.isProcessing = true;
      this.validationErrors = {};

      try {
        const { revision, meta } = await this.$store.dispatch(REOPEN_LATEST_STOCK_REVISION, {
          warehouse_id: this.form.warehouse_id,
        });
        this.applyRevision(revision);
        this.replaceRouteWithRevision();
        this.step = 2;
        this.reportReallocationFailures(meta);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.REVISIONS.REOPENED"),
          type: "success",
        });
      } catch (error) {
        this.validationErrors = (error && error.errors) || {};
        this.showError(error);
      } finally {
        this.isProcessing = false;
      }
    },
    continueOpenDraft() {
      if (!this.openDraft) {
        return;
      }
      this.loadRevision(this.openDraft.id).then(() => this.replaceRouteWithRevision());
    },
    replaceRouteWithRevision() {
      if (!this.revision || Number(this.$route.params.id) === Number(this.revision.id)) {
        return;
      }
      this.$router.replace({
        name: "stock-revisions-edit",
        params: { id: this.revision.id },
      });
    },
    flushCountSheet() {
      const sheet = this.$refs.countSheet;
      if (!sheet) {
        return Promise.resolve(true);
      }
      return sheet.flush();
    },
    async acceptDefaultCounts() {
      if (!this.revision) {
        return;
      }
      await this.$store.dispatch(ACCEPT_STOCK_REVISION_DEFAULT_COUNTS, this.revision.id);
    },
    async goToStep(step) {
      if (step === this.step) {
        return;
      }

      if (this.step === 2) {
        const saved = await this.flushCountSheet();
        if (!saved) {
          return;
        }
      }

      if (step > 1 && !this.revision) {
        return;
      }

      if (step === 3) {
        this.isProcessing = true;
        try {
          // Persist Ideal→Actual defaults for every product still uncounted (all pages).
          await this.acceptDefaultCounts();
          this.reviewSheetKey += 1;
          await this.loadReview();
        } catch (error) {
          this.showError(error);
          return;
        } finally {
          this.isProcessing = false;
        }
      }

      this.step = step;
    },
    async loadReview() {
      try {
        const [summary, warnings] = await Promise.all([
          this.$store.dispatch(GET_STOCK_REVISION_SUMMARY, { id: this.revision.id }),
          this.$store.dispatch(GET_STOCK_REVISION_WARNINGS, this.revision.id),
        ]);
        this.summary = summary;
        this.warnings = warnings || [];
      } catch (error) {
        this.showError(error);
      }
    },
    async finalizeRevision() {
      this.isProcessing = true;
      try {
        await this.acceptDefaultCounts();
        const { revision, meta } = await this.$store.dispatch(
          FINALIZE_STOCK_REVISION,
          this.revision.id
        );
        this.revision = revision;
        this.hasUnsavedCounts = false;
        this.reportReallocationFailures(meta);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.REVISIONS.FINALIZED"),
          type: "success",
        });
        this.$router.push({ name: "stock-revisions-view", params: { id: revision.id } });
      } catch (error) {
        this.showError(error);
      } finally {
        this.isProcessing = false;
      }
    },
    async deleteDraft() {
      this.isProcessing = true;
      try {
        await this.$store.dispatch(DELETE_STOCK_REVISION, this.revision.id);
        this.hasUnsavedCounts = false;
        this.revision = null;
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.REVISIONS.DELETED"),
          type: "success",
        });
        this.$router.push({ name: "stock-revisions" });
      } catch (error) {
        this.showError(error);
      } finally {
        this.isProcessing = false;
      }
    },
    reportReallocationFailures(meta) {
      const failures = (meta && meta.reallocation_failures) || [];
      if (!failures.length) {
        return;
      }

      this.$store.dispatch(SHOW_SNACK, {
        message: `${this.$t("STOCK.REVISIONS.REALLOCATION_FAILURES.TITLE")}: ${failures.join(" | ")}`,
        type: "warning",
      });
    },
    warningProductLabel(warning) {
      if (warning.product_code && warning.product_name) {
        return `${warning.product_code} — ${warning.product_name}`;
      }
      return warning.product_name || warning.product_code || `#${warning.stock_product_id}`;
    },
    showError(error) {
      this.$store.dispatch(SHOW_SNACK, {
        message: (error && error.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
        type: "error",
      });
    },
  },
};
</script>
